import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip
} from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';

const GpxFitTraceabilitySection = ({ gpxSegments, fitChunks }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [selectedGpxSegment, setSelectedGpxSegment] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const apiRef = useGridApiRef();

  // Scroll to selected row when segment changes
  useEffect(() => {
    if (selectedGpxSegment && apiRef.current && gpxSegments) {
      setTimeout(() => {
        try {
          apiRef.current.scrollToIndexes({ rowIndex: gpxSegments.findIndex(s => s.segment_id === selectedGpxSegment.segment_id) });
        } catch (error) {

        }
      }, 500);
    }
  }, [selectedGpxSegment, paginationModel.page, gpxSegments, apiRef]);

  // GPX Segments columns
  const gpxColumns = [
    { 
      field: 'segment_id', 
      headerName: t('segmentId'), 
      width: 100,
      type: 'number'
    },
    { 
      field: 'distance_start', 
      headerName: t('startM'), 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'distance_end', 
      headerName: t('endM'), 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'distance_length', 
      headerName: t('lengthM'), 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'avg_gradient', 
      headerName: t('gradientPercent'), 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2),
      cellClassName: (params) => {
        if (params.value > 5) return 'gradient-steep-climb';
        if (params.value > 2) return 'gradient-climb';
        if (params.value < -5) return 'gradient-steep-descent';
        if (params.value < -2) return 'gradient-descent';
        return 'gradient-flat';
      }
    },
    { 
      field: 'avg_power', 
      headerName: t('powerW'), 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'avg_speed', 
      headerName: t('speedKmh'), 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2)
    },
    { 
      field: 'match_method', 
      headerName: t('matchMethod'), 
      width: 150
    },
    { 
      field: 'speed_calculation_method', 
      headerName: t('speedMethod'), 
      width: 150
    }
  ];

  // FIT Chunks columns
  const fitColumns = [
    { 
      field: 'chunk_index', 
      headerName: t('chunkIndex'), 
      width: 110,
      type: 'number'
    },
    { 
      field: 'timestamp', 
      headerName: t('timestamp'), 
      width: 180
    },
    { 
      field: 'distance', 
      headerName: t('distanceM'), 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2)
    },
    { 
      field: 'avg_gradient', 
      headerName: t('gradientPercent'), 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2),
      cellClassName: (params) => {
        if (params.value > 5) return 'gradient-steep-climb';
        if (params.value > 2) return 'gradient-climb';
        if (params.value < -5) return 'gradient-steep-descent';
        if (params.value < -2) return 'gradient-descent';
        return 'gradient-flat';
      }
    },
    { 
      field: 'avg_power', 
      headerName: t('powerW'), 
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(1)
    },
    { 
      field: 'avg_speed', 
      headerName: t('speedKmh'), 
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2)
    },
    { 
      field: 'assumed_cda', 
      headerName: t('cda'), 
      width: 90,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(3)
    },
    { 
      field: 'weight', 
      headerName: t('weight'), 
      width: 90,
      type: 'number',
      valueFormatter: (params) => params.value?.toFixed(2),
      description: t('zoneBasedWeightDescription')
    },
    { 
      field: 'zone', 
      headerName: t('zone'), 
      width: 80,
      type: 'number',
      renderCell: (params) => {
        const zoneLabels = [t('zoneFirst'), t('zoneSecond'), t('zoneThird')];
        return zoneLabels[params.value] || 'N/A';
      }
    }
  ];

  // Prepare GPX rows with ID
  const gpxRows = gpxSegments?.map((segment) => ({
    id: segment.segment_id,
    ...segment
  })) || [];

  // Get matched FIT chunks for selected GPX segment
  const getMatchedFitChunks = () => {
    if (!selectedGpxSegment || !selectedGpxSegment.matched_fit_chunk_indices) {
      return [];
    }

    const indices = selectedGpxSegment.matched_fit_chunk_indices;
    const weights = selectedGpxSegment.matched_fit_chunk_weights || [];
    
    return indices.map((chunkIndex, i) => {
      const chunk = fitChunks[chunkIndex];
      if (!chunk) return null;
      
      // Calculate zone for this chunk
      const totalChunks = fitChunks.length;
      const zoneSize = totalChunks / 3;
      const zone = Math.min(2, Math.floor(chunkIndex / zoneSize));
      
      return {
        id: chunkIndex,
        chunk_index: chunkIndex,
        timestamp: chunk.timestamp,
        distance: chunk.distance,
        avg_gradient: chunk.avg_gradient,
        avg_power: chunk.avg_power,
        avg_speed: chunk.avg_speed,
        assumed_cda: chunk.assumed_cda,
        weight: weights[i],
        zone: zone
      };
    }).filter(Boolean);
  };

  const matchedFitRows = getMatchedFitChunks();

  // Prepare chart data for GPX segments
  const gpxChartData = useMemo(() => {
    if (!gpxSegments) return [];
    
    return gpxSegments.map((segment) => {
      const distanceKm = segment.distance_start / 1000;
      return {
        distance: distanceKm,
        altitude: segment.elevation_start,
        segment_id: segment.segment_id,
        gradient: segment.avg_gradient
      };
    });
  }, [gpxSegments]);

  // Prepare chart data for FIT chunks
  const fitChartData = useMemo(() => {
    if (!fitChunks) return [];
    
    let cumulativeDistance = 0;
    return fitChunks.map((chunk, index) => {
      const distanceKm = cumulativeDistance / 1000;
      cumulativeDistance += chunk.distance;
      return {
        distance: distanceKm,
        altitude: chunk.avg_altitude,
        chunk_index: index,
        gradient: chunk.avg_gradient
      };
    });
  }, [fitChunks]);

  // Get highlighted points for selected segment
  const getHighlightedGpxPoint = () => {
    if (!selectedGpxSegment) return null;
    return {
      distance: selectedGpxSegment.distance_start / 1000,
      altitude: selectedGpxSegment.elevation_start
    };
  };

  // Get highlighted points for matched chunks
  const getHighlightedFitPoints = () => {
    if (!selectedGpxSegment || !selectedGpxSegment.matched_fit_chunk_indices) {
      return [];
    }
    
    return selectedGpxSegment.matched_fit_chunk_indices.map(index => {
      const dataPoint = fitChartData[index];
      return dataPoint ? {
        distance: dataPoint.distance,
        altitude: dataPoint.altitude,
        chunk_index: index
      } : null;
    }).filter(Boolean);
  };

  const highlightedGpxPoint = getHighlightedGpxPoint();
  const highlightedFitPoints = getHighlightedFitPoints();

  // Handle click on GPX chart to select segment
  const handleGpxChartClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedSegmentId = data.activePayload[0].payload.segment_id;
      const segment = gpxSegments.find(s => s.segment_id === clickedSegmentId);
      if (segment) {
        setSelectedGpxSegment(segment);
        const segmentIndex = gpxSegments.findIndex(s => s.segment_id === clickedSegmentId);
        const targetPage = Math.floor(segmentIndex / paginationModel.pageSize);
        setPaginationModel({ ...paginationModel, page: targetPage });
      }
    }
  };

  if (!gpxSegments || gpxSegments.length === 0) {
    return null;
  }

  return (
    <Box mt={4}>
      <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
        {t('gpxFitTraceability')}
      </Typography>
      <Typography variant="body2" color={colors.grey[300]} mb={3}>
        {t('selectGpxSegmentPrompt')}
      </Typography>

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Altitude Charts Section */}
        <Box display="flex" flexDirection="column" gap={2}>
          {/* GPX Altitude Chart */}
          <Box
            paddingTop={1}
            paddingBottom={1}
            sx={{
              backgroundColor: colors.grey[600],
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
              {t('gpxAltitudeProfile')}
            </Typography>
            <Box height={400} bgcolor={colors.grey[800]} borderRadius="8px" p={2} mx={2}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={gpxChartData} onClick={handleGpxChartClick}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                  <XAxis 
                    dataKey="distance" 
                    stroke={colors.grey[200]}
                    label={{ value: 'Distance (km)', position: 'insideBottom', offset: -5, fill: colors.grey[200] }}
                  />
                  <YAxis 
                    stroke={colors.grey[200]}
                    label={{ value: 'Altitude (m)', angle: -90, position: 'insideLeft', fill: colors.grey[200] }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: colors.grey[800], 
                      border: `1px solid ${colors.grey[700]}`,
                      borderRadius: '4px'
                    }}
                    labelStyle={{ color: colors.grey[100] }}
                    formatter={(value, name) => {
                      if (name === 'altitude') return [`${value.toFixed(1)} m`, 'Altitude'];
                      return [value, name];
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="altitude" 
                    stroke={colors.blueAccent[500]} 
                    dot={false}
                    strokeWidth={2}
                  />
                  {highlightedGpxPoint && (
                    <ReferenceDot
                      x={highlightedGpxPoint.distance}
                      y={highlightedGpxPoint.altitude}
                      r={8}
                      fill={colors.redAccent[500]}
                      stroke={colors.grey[100]}
                      strokeWidth={2}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* FIT Altitude Chart */}
          <Box
            paddingTop={1}
            paddingBottom={1}
            sx={{
              backgroundColor: colors.grey[600],
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
              {t('fitAltitudeProfile')}
            </Typography>
            <Box height={400} bgcolor={colors.grey[800]} borderRadius="8px" p={2} mx={2}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={fitChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                  <XAxis 
                    dataKey="distance" 
                    stroke={colors.grey[200]}
                    label={{ value: 'Distance (km)', position: 'insideBottom', offset: -5, fill: colors.grey[200] }}
                  />
                  <YAxis 
                    stroke={colors.grey[200]}
                    label={{ value: 'Altitude (m)', angle: -90, position: 'insideLeft', fill: colors.grey[200] }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: colors.grey[800], 
                      border: `1px solid ${colors.grey[700]}`,
                      borderRadius: '4px'
                    }}
                    labelStyle={{ color: colors.grey[100] }}
                    formatter={(value, name) => {
                      if (name === 'altitude') return [`${value.toFixed(1)} m`, 'Altitude'];
                      return [value, name];
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="altitude" 
                    stroke={colors.greenAccent[500]} 
                    dot={false}
                    strokeWidth={2}
                  />
                  {highlightedFitPoints.map((point, index) => (
                    <ReferenceDot
                      key={`fit-${point.chunk_index}-${index}`}
                      x={point.distance}
                      y={point.altitude}
                      r={6}
                      fill={colors.redAccent[500]}
                      stroke={colors.grey[100]}
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        {/* GPX Segments Grid */}
        <Box
          paddingTop={1}
          paddingBottom={1}
          sx={{
            backgroundColor: colors.grey[600],
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
            {t('gpxSegments')} ({gpxRows.length})
          </Typography>
          <Box 
            className="gpx-segments-grid"
            sx={{ 
              height: 500,
              width: '100%',
              px: 2,
              '& .gradient-steep-climb': {
                color: colors.redAccent[500]
              },
              '& .gradient-climb': {
                color: colors.redAccent[300]
              },
              '& .gradient-flat': {
                color: colors.grey[200]
              },
              '& .gradient-descent': {
                color: colors.greenAccent[400]
              },
              '& .gradient-steep-descent': {
                color: colors.greenAccent[600]
              }
            }}
          >
            <DataGrid
              apiRef={apiRef}
              rows={gpxRows}
              columns={gpxColumns}
              checkboxSelection={false}
              disableMultipleRowSelection
              onRowClick={(params) => setSelectedGpxSegment(params.row)}
              rowSelectionModel={selectedGpxSegment ? [selectedGpxSegment.segment_id] : []}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              sx={{
                border: 0,
                color: `${colors.grey[200]} !important`,
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none'
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: colors.grey[800],
                  borderBottom: '2px solid ' + colors.grey[700]
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold'
                },
                '& .MuiDataGrid-virtualScroller': {
                  backgroundColor: colors.grey[800]
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '2px solid ' + colors.grey[700],
                  backgroundColor: colors.grey[700]
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: colors.grey[700]
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: colors.blueAccent[800],
                  '&:hover': {
                    backgroundColor: colors.blueAccent[700]
                  }
                }
              }}
            />
          </Box>
        </Box>

        {/* Matched FIT Chunks Grid */}
        <Box
          paddingTop={1}
          paddingBottom={1}
          sx={{
            backgroundColor: colors.grey[600],
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1} px={2}>
            {t('matchedFitChunks')} 
            {selectedGpxSegment && ` ${t('forSegment', { id: selectedGpxSegment.segment_id })} ${t('chunksCount', { count: matchedFitRows.length })}`}
          </Typography>
          {selectedGpxSegment && (
            <Box mb={2} display="flex" gap={2} flexWrap="wrap" px={2}>
              <Chip 
                label={`${t('method')}: ${selectedGpxSegment.match_method || 'N/A'}`} 
                color="primary"
              />
              {selectedGpxSegment.match_tolerance && (
                <Chip 
                  label={`${t('tolerance')}: ${selectedGpxSegment.match_tolerance.toFixed(2)}%`} 
                  color="secondary"
                />
              )}
              <Chip 
                label={`${t('basePower')}: ${selectedGpxSegment.base_avg_power?.toFixed(1) || 'N/A'}W`} 
                color="default"
              />
              <Chip 
                label={`${t('finalPower')}: ${selectedGpxSegment.avg_power?.toFixed(1) || 'N/A'}W`} 
                color="default"
              />
              {selectedGpxSegment.estimated_power && (
                <Chip 
                  label={`${t('estimatedPower')}: ${selectedGpxSegment.estimated_power.toFixed(1)}W`} 
                  color="warning"
                />
              )}
              <Chip 
                label={`${t('speedMethod')}: ${selectedGpxSegment.speed_calculation_method || 'N/A'}`} 
                color="info"
              />
            </Box>
          )}
          <Box 
            sx={{ 
              height: 500,
              width: '100%',
              px: 2,
              '& .gradient-steep-climb': {
                color: colors.redAccent[500]
              },
              '& .gradient-climb': {
                color: colors.redAccent[300]
              },
              '& .gradient-flat': {
                color: colors.grey[200]
              },
              '& .gradient-descent': {
                color: colors.greenAccent[400]
              },
              '& .gradient-steep-descent': {
                color: colors.greenAccent[600]
              }
            }}
          >
            {!selectedGpxSegment ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height="100%" 
                bgcolor={colors.grey[800]}
                borderRadius="4px"
              >
                <Typography variant="body1" color={colors.grey[300]}>
                  {t('selectSegmentToView')}
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={matchedFitRows}
                columns={fitColumns}
                checkboxSelection={false}
                pageSizeOptions={[25, 50, 100]}
                sx={{
                  border: 0,
                  color: `${colors.grey[200]} !important`,
                  '& .MuiDataGrid-cell': {
                    borderBottom: 'none'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.grey[800],
                    borderBottom: '2px solid ' + colors.grey[700]
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold'
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    backgroundColor: colors.grey[800]
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid ' + colors.grey[700],
                    backgroundColor: colors.grey[700]
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: colors.grey[700]
                  }
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GpxFitTraceabilitySection;
